import React, { useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import isEqual from 'lodash/isEqual';
import strings from 'strings';
import { TextBox, When } from 'UI/components/atoms';
import FPHtmlViewer from 'UI/components/atoms/FPHtmlViewer';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import EmailPicker from 'UI/components/molecules/EmailPicker';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { idOptionSelected, REQUIRED_VALIDATION } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

import { EMAIL_DETAILS_FORM_FIELDS_MAP as FORM_FIELDS_MAP } from './EmailDetails.fields';
import { useStyles } from './EmailDetails.styled';
import { buildResolvedEmailBody } from './EmailDetails.utils';
import { TemplateEditor } from './TemplateEditor';

export const EmailDetails = ({
  jobOrder = {},
  candidateInfo = {},
  templateId,
  canDisableEmailToHiringAuthority = false
}) => {
  const classes = useStyles();
  const { control, errors, watch, setValue } = useFormContext();

  const handleOnChange = useCallback(([, value]) => value, []);

  const cancelEmailToHiringAuthority = watch(FORM_FIELDS_MAP.CancelEmailToHiringAuthority.key);
  const accountableRecruiter = watch(FORM_FIELDS_MAP.AccountableRecruiter.key);
  const accountableRecruiterEmail = accountableRecruiter?.email;
  const emailBody = watch(FORM_FIELDS_MAP.EmailBody.key);
  const resolvedEmailBody = watch(FORM_FIELDS_MAP.ResolvedEmailBody.key);
  const hiringAuthority = watch(FORM_FIELDS_MAP.HiringAuthority.key);
  const candidate = watch(FORM_FIELDS_MAP.Candidate.key);
  const interviewData = watch([
    FORM_FIELDS_MAP.InterviewDate.key,
    FORM_FIELDS_MAP.InterviewFromHour.key,
    FORM_FIELDS_MAP.InterviewToHour.key,
    FORM_FIELDS_MAP.InterviewTimeFrame.key,
    FORM_FIELDS_MAP.InterviewTimezone.key
  ]);

  const hiringAuthorities = jobOrder?.hiringAuthorities || [];
  const defaultEmailSubject = `Interview Confirmation${
    candidate?.fullName ? ` of ${candidate.fullName}` : ''
  }`;
  const previewEmailData = buildResolvedEmailBody({
    emailBody,
    hiringAuthority,
    candidate,
    interviewData,
    jobOrder,
    candidateInfo
  });
  const previewEmailBody = previewEmailData.resolvedEmailBody;

  useEffect(() => {
    const nextBlindCarbonCopy = accountableRecruiterEmail
      ? [{ work_email: accountableRecruiterEmail }]
      : [];

    setValue(FORM_FIELDS_MAP.BlindCarbonCopy.key, nextBlindCarbonCopy);
  }, [accountableRecruiterEmail, setValue]);

  useEffect(() => {
    if (isEqual(resolvedEmailBody, previewEmailBody)) return;

    setValue(FORM_FIELDS_MAP.ResolvedEmailBody.key, previewEmailBody);
  }, [previewEmailBody, resolvedEmailBody, setValue]);

  return (
    <Grid item container spacing={1}>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.HiringAuthority.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={null}
          as={
            <AutocompleteSelect
              error={!!errors[FORM_FIELDS_MAP.HiringAuthority.key]}
              errorText={errors[FORM_FIELDS_MAP.HiringAuthority.key]?.message}
              placeholder={strings.sendouts.form.labels.hiringAuthority}
              renderOption={OptionRenderers.displayHiringAuthority}
              getOptionSelected={idOptionSelected}
              displayKey="full_name"
              defaultOptions={hiringAuthorities}
            />
          }
          onChange={handleOnChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />

        <When condition={canDisableEmailToHiringAuthority}>
          <Controller
            name={FORM_FIELDS_MAP.CancelEmailToHiringAuthority.key}
            control={control}
            defaultValue={false}
            as={
              <FormControlLabel
                label="Don't send email to Hiring Authority"
                labelPlacement="end"
                control={<Checkbox color="primary" />}
                classes={{ label: classes.formControlLabel }}
              />
            }
            onChange={handleOnChange}
            onChangeName="onChange"
            valueName="checked"
          />
        </When>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.CarbonCopy.key}
          control={control}
          defaultValue={[]}
          as={
            <EmailPicker
              disabled={!!cancelEmailToHiringAuthority}
              placeholder="CC"
              label="CC"
              error={!!errors[FORM_FIELDS_MAP.CarbonCopy.key]}
              errorText={errors[FORM_FIELDS_MAP.CarbonCopy.key]?.message}
              endpoint={Endpoints.Search}
              typeaheadParams={{
                entityType: EntityType.HiringAuthority,
                inColumns: ['ha.work_email']
              }}
              maxItems={10}
              allowNewItems
            />
          }
          onChange={handleOnChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.BlindCarbonCopy.key}
          control={control}
          defaultValue={[]}
          as={
            <EmailPicker
              disabled // TODO: legacy implementation has disabled logic. We need to confirm this behavior
              placeholder="BCC"
              label="BCC"
              error={!!errors[FORM_FIELDS_MAP.BlindCarbonCopy.key]}
              errorText={errors[FORM_FIELDS_MAP.BlindCarbonCopy.key]?.message}
              endpoint={Endpoints.Search}
              typeaheadParams={{
                entityType: EntityType.HiringAuthority,
                inColumns: ['ha.work_email']
              }}
              maxItems={5}
              allowNewItems
            />
          }
          onChange={handleOnChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.EmailSubject.key}
          defaultValue={defaultEmailSubject}
          rules={REQUIRED_VALIDATION}
          control={control}
          as={
            <TextBox
              label="Subject *"
              placeholder="Subject *"
              error={!!errors[FORM_FIELDS_MAP.EmailSubject.key]}
              errorText={errors[FORM_FIELDS_MAP.EmailSubject.key]?.message}
            />
          }
          onChange={handleOnChange}
          onChangeName="onChange"
          valueName="value"
        />
      </Grid>
      <Grid item xs={12}>
        <TemplateEditor
          name={FORM_FIELDS_MAP.EmailBody.key}
          endpoint={`${Endpoints.SendoutsTemplates}?version=2`}
          templateId={templateId}
        />
        <FPHtmlViewer className={classes.previewEmailContent} content={previewEmailBody} />
      </Grid>
    </Grid>
  );
};
